import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchGitHubEvents } from '../../lib/api/activity';
import { fetchProjectRepos } from '../../lib/api/projects';
import { StatusBadge, toneForGitHubEventOutcome, labelForGitHubEventOutcome } from '../ui/StatusBadge';
import type { GitHubEventItem } from '../../../../shared/types/epics';

/**
 * 2026-07-16 — GitHub webhook activity log viewer.
 *
 * Cloned from AuditTab's table/pagination pattern. Shows one row per webhook
 * delivery Forge received for a repo — including deliveries that were
 * dropped or rejected — so "I posted a comment and nothing happened" has a
 * concrete answer in the portal instead of only in CloudWatch logs. This is
 * what SecureNX/DataHub#11's /init incident was missing: the webhook DID
 * arrive, but the only trace of the rejection was a log line no one could see.
 */
export const GitHubEventsTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const reposQ = useQuery({
    queryKey: ['project', projectId, 'repos'],
    queryFn: () => fetchProjectRepos(projectId),
    enabled: projectId.length > 0,
  });

  const repos = reposQ.data ?? [];
  const activeRepo = selectedRepo ?? repos[0]?.fullName ?? null;
  const cursor = cursorStack[cursorStack.length - 1];

  const eventsQ = useQuery({
    queryKey: ['project', projectId, 'github-events', activeRepo, cursor],
    queryFn: () => fetchGitHubEvents(activeRepo as string, { limit: 50, cursor }),
    enabled: !!activeRepo,
  });

  const handleRepoChange = (fullName: string) => {
    setSelectedRepo(fullName);
    setCursorStack([]);
  };

  return (
    <div className="space-y-3">
      <div className="border rounded p-2 flex flex-wrap items-end gap-2 text-xs">
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-gray-500">repo</span>
          {reposQ.isLoading ? (
            <span className="text-gray-500">Loading repos…</span>
          ) : repos.length === 0 ? (
            <span className="text-gray-500">No repos attached yet.</span>
          ) : (
            <select
              value={activeRepo ?? ''}
              onChange={(e) => handleRepoChange(e.target.value)}
              className="border rounded px-1 py-0.5 bg-white font-mono"
            >
              {repos.map((r) => (
                <option key={r.fullName} value={r.fullName}>{r.fullName}</option>
              ))}
            </select>
          )}
        </label>
      </div>

      {!activeRepo && !reposQ.isLoading && (
        <div className="border rounded p-6 text-center text-sm text-gray-500 bg-gray-50">
          Attach a repo to see its GitHub webhook activity.
        </div>
      )}

      {activeRepo && eventsQ.isLoading && (
        <div className="text-sm text-gray-500">Loading GitHub event activity…</div>
      )}
      {activeRepo && eventsQ.isError && (
        <div role="alert" className="text-sm text-red-600">
          {(eventsQ.error as Error).message}
        </div>
      )}
      {activeRepo && eventsQ.isSuccess && eventsQ.data.events.length === 0 && (
        <div
          data-testid="github-events-empty"
          className="border rounded p-6 text-center text-sm text-gray-500 bg-gray-50"
        >
          No GitHub webhook activity recorded yet for this repo.
        </div>
      )}

      {activeRepo && eventsQ.isSuccess && eventsQ.data.events.length > 0 && (
        <div className="border rounded overflow-x-auto" data-testid="github-events-table">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-2 py-1.5 font-medium">When</th>
                <th className="px-2 py-1.5 font-medium">Event</th>
                <th className="px-2 py-1.5 font-medium">Actor</th>
                <th className="px-2 py-1.5 font-medium">Issue</th>
                <th className="px-2 py-1.5 font-medium">Comment</th>
                <th className="px-2 py-1.5 font-medium">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {eventsQ.data.events.map((ev, i) => (
                <GitHubEventRow key={`${ev.createdAt}-${i}`} event={ev} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeRepo && eventsQ.isSuccess && (
        <div className="flex justify-center gap-2">
          {cursorStack.length > 0 && (
            <button
              type="button"
              onClick={() => setCursorStack((prev) => prev.slice(0, -1))}
              className="text-xs border rounded px-3 py-1 hover:bg-gray-50"
            >
              Newer
            </button>
          )}
          {eventsQ.data.nextCursor && (
            <button
              type="button"
              onClick={() => setCursorStack((prev) => [...prev, eventsQ.data.nextCursor as string])}
              className="text-xs border rounded px-3 py-1 hover:bg-gray-50"
            >
              Load older entries
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const GitHubEventRow: React.FC<{ event: GitHubEventItem }> = ({ event }) => (
  <tr className="border-t hover:bg-gray-50 align-top">
    <td className="px-2 py-1 font-mono whitespace-nowrap">
      {event.createdAt ? new Date(event.createdAt).toLocaleString() : '—'}
    </td>
    <td className="px-2 py-1">
      <span className="inline-block bg-gray-100 px-1.5 py-0.5 rounded text-[10px] font-mono">
        {event.eventType}{event.action ? `.${event.action}` : ''}
      </span>
    </td>
    <td className="px-2 py-1 font-mono">{event.actor ?? '—'}</td>
    <td className="px-2 py-1">
      {event.issueNumber != null ? (
        event.issueUrl ? (
          <a
            href={event.issueUrl}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline font-mono"
          >
            #{event.issueNumber}
          </a>
        ) : (
          <span className="font-mono">#{event.issueNumber}</span>
        )
      ) : (
        '—'
      )}
    </td>
    <td className="px-2 py-1 max-w-[280px] truncate" title={event.commentPreview ?? undefined}>
      {event.commentPreview ?? '—'}
    </td>
    <td className="px-2 py-1">
      <div className="flex flex-col gap-0.5 items-start">
        <StatusBadge tone={toneForGitHubEventOutcome(event.outcome)}>
          {labelForGitHubEventOutcome(event.outcome)}
        </StatusBadge>
        {event.reason && (
          <span className="text-[10px] text-gray-500 max-w-[220px] truncate" title={event.reason}>
            {event.reason}
          </span>
        )}
      </div>
    </td>
  </tr>
);

export default GitHubEventsTab;
